import React, { useState, useEffect } from 'react';
import { Table, Form, Badge, Button, Dropdown, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaEdit, FaCheckCircle, FaUserTag, FaInfoCircle } from 'react-icons/fa';
import { formatDate } from '../utils/dateFormatter';
import userApi from '../services/userApi';
import EditTaskModal from './EditTaskModal';
import GuestCheckoutLine from './GuestCheckoutLine';
import useLanguage from '../hooks/useLanguage';

const TaskListTable = ({
  tasks,
  onStatusUpdate,
  onAssign,
  onComplete,
  onBulkComplete,
  onEdit,
  selectedTasks,
  setSelectedTasks
}) => {
  const { t, isArabic, language } = useLanguage();

  const PRIORITY_STYLES = {
    critical: { emoji: '🔴', label: t('tasks.priority.label.critical') },
    urgent: { emoji: '🟠', label: t('tasks.priority.label.urgent') },
    high: { emoji: '🟡', label: t('tasks.priority.label.high') },
    medium: { emoji: '🟢', label: t('tasks.priority.label.medium') },
    low: { emoji: '🔵', label: t('tasks.priority.label.low') }
  };

  const STATUS_BADGES = {
    pending: { bg: 'light', text: 'dark', label: t('tasks.status.pending') },
    assigned: { bg: 'light', text: 'primary', label: t('tasks.status.assigned') },
    'in-progress': { bg: 'light', text: 'info', label: t('tasks.status.inProgress') },
    completed: { bg: 'light', text: 'success', label: t('tasks.status.completed') },
    cancelled: { bg: 'light', text: 'danger', label: t('tasks.status.cancelled') }
  };

  const [selectAll, setSelectAll] = useState(false);
  const [maintenanceUsers, setMaintenanceUsers] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  useEffect(() => {
    loadMaintenanceUsers();
  }, []);

  const loadMaintenanceUsers = async () => {
    try {
      const response = await userApi.getStaffUsers();
      setMaintenanceUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load maintenance users:', error);
    }
  };

  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedTasks(tasks.map(t => t._id));
    } else {
      setSelectedTasks([]);
    }
  };

  const handleSelectTask = (taskId) => {
    if (selectedTasks.includes(taskId)) {
      setSelectedTasks(selectedTasks.filter(id => id !== taskId));
    } else {
      setSelectedTasks([...selectedTasks, taskId]);
    }
  };

  const isTaskSelected = (taskId) => selectedTasks.includes(taskId);

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowEditModal(true);
  };

  const handleTaskUpdated = () => {
    setShowEditModal(false);
    setEditingTask(null);
    if (onEdit) {
      onEdit(); // Reload tasks from parent
    }
  };

  return (
    <>
      {selectedTasks.length > 0 && (
        <div className="mb-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
          <span>{selectedTasks.length} {t('tasks.table.bulk.tasksSelected')}</span>
          <ButtonGroup size="sm">
            <Dropdown as={ButtonGroup}>
              <Dropdown.Toggle variant="primary" size="sm">
                <FaUserTag className="ms-1" /> {t('tasks.table.bulk.assignTo')}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {maintenanceUsers.map(user => (
                  <Dropdown.Item key={user._id} onClick={() => onAssign(selectedTasks, user._id)}>
                    {user.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                // The page decides what bulk close may close (guest question) — see Tasks.jsx.
                onBulkComplete(selectedTasks);
                setSelectedTasks([]);
              }}
            >
              <FaCheckCircle className="ms-1" /> {t('tasks.table.bulk.markComplete')}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setSelectedTasks([])}
            >
              {t('tasks.table.bulk.cancelSelection')}
            </Button>
          </ButtonGroup>
        </div>
      )}

      <div className="task-list-scroll">
        <Table hover className="align-middle task-list-table" dir={isArabic ? 'rtl' : 'ltr'}>
          <thead className="table-light">
            <tr>
              <th style={{ width: '40px' }}>
                <Form.Check
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </th>
              <th>{t('tasks.table.headers.task')}</th>
              <th>{t('tasks.table.headers.property')}</th>
              <th className="hide-mobile-sm">{t('tasks.table.ticketRef')}</th>
              <th>
                {t('tasks.table.headers.priority')}{' '}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <div className="text-end" style={{ fontSize: '0.75rem' }}>
                        <div>🔴 <strong>{t('tasks.priority.label.critical')}:</strong> {t('tasks.table.tooltip.critical')}</div>
                        <div>🟠 <strong>{t('tasks.priority.label.urgent')}:</strong> {t('tasks.table.tooltip.urgent')}</div>
                        <div>🟡 <strong>{t('tasks.priority.label.high')}:</strong> {t('tasks.table.tooltip.high')}</div>
                        <div>🟢 <strong>{t('tasks.priority.label.medium')}:</strong> {t('tasks.table.tooltip.medium')}</div>
                        <div>🔵 <strong>{t('tasks.priority.label.low')}:</strong> {t('tasks.table.tooltip.low')}</div>
                      </div>
                    </Tooltip>
                  }
                >
                  <FaInfoCircle className="text-muted" style={{ cursor: 'pointer', fontSize: '0.85rem' }} />
                </OverlayTrigger>
              </th>
              <th>{t('tasks.table.headers.status')}</th>
              <th>{t('tasks.table.headers.scheduledDate')}</th>
              <th className="hide-mobile-sm">{t('tasks.table.headers.assignedTo')}</th>
              <th>{t('tasks.table.headers.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map(task => (
              <tr
                key={task._id}
                className={task.isOverdue ? 'table-warning' : ''}
              >
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={isTaskSelected(task._id)}
                    onChange={() => handleSelectTask(task._id)}
                  />
                </td>
                <td>
                  <div>
                    <strong>{task.title}</strong>
                    {task.taskSource === 'guest' && (
                      // Plain span (not <Badge>) so Bootstrap's bg-primary
                      // !important rule doesn't override the brand-purple color.
                      <span
                        className="badge ms-2 fw-normal text-white"
                        style={{ backgroundColor: '#471396', fontSize: '0.7em' }}
                        title={t('tasks.guestRequestBadge')}
                      >
                        <i className="bi bi-chat-left-quote me-1"></i>
                        {t('tasks.guestRequestBadge')}
                      </span>
                    )}
                    {task.notes && (
                      <div className="small text-muted text-truncate" style={{ maxWidth: '200px' }}>
                        {task.notes}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  {task.displayLocationName ? (
                    <div>
                      <div>
                        {task.displayLocationName}
                        {task.displayLocationCode && (
                          <span className="badge bg-light text-dark ms-2 fw-normal" style={{ fontSize: '0.75em' }} dir="ltr">{task.displayLocationCode}</span>
                        )}
                      </div>
                      {task.property?.address && (
                        <small className="text-muted">{task.property.address}</small>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td className="hide-mobile-sm">
                  {task.ticketId ? (
                    <div>
                      <div className="small text-primary fw-semibold">{task.ticketId}</div>
                      {task.booking?.confirmationCode && (
                        <div className="small text-muted">{task.booking.confirmationCode}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  <span>
                    {PRIORITY_STYLES[task.priority]?.emoji} {PRIORITY_STYLES[task.priority]?.label || task.priority}
                  </span>
                </td>
                <td>
                  <Badge bg={STATUS_BADGES[task.status]?.bg} text={STATUS_BADGES[task.status]?.text}>
                    {STATUS_BADGES[task.status]?.label || task.status}
                  </Badge>
                  {task.isOverdue && (
                    <Badge bg="danger" className="me-1">{t('tasks.table.overdue')}</Badge>
                  )}
                </td>
                <td>
                  <div>
                    {formatDate(task.scheduledDate, {}, language)}
                    {task.scheduledTime && (
                      <div className="small text-muted">{task.scheduledTime}</div>
                    )}
                    {/* Guest checkout — inside the SCHEDULE cell rather than as a ninth column.
                        It answers the same question the two lines above pose ("when is this room
                        free?") and belongs beside them; a separate column would need a header, a
                        width and a mobile-hiding decision for a field that is empty on most rows.
                        Compact: the recorder's name is one click away in the edit modal (D43). */}
                    {task.guestCheckout && (
                      <GuestCheckoutLine checkout={task.guestCheckout} compact className="small" />
                    )}
                  </div>
                </td>
                <td className="hide-mobile-sm">
                  {task.assignedTo ? (
                    <span>{task.assignedTo.name || task.assignedTo}</span>
                  ) : (
                    <Badge bg="warning" text="dark">{t('tasks.table.unassigned')}</Badge>
                  )}
                </td>
                <td>
                  <ButtonGroup size="sm">
                    <Button
                      variant="outline-primary"
                      title={t('tasks.table.editTooltip')}
                      onClick={() => handleEditTask(task)}
                    >
                      <FaEdit />
                    </Button>
                    <Dropdown as={ButtonGroup}>
                      <Dropdown.Toggle
                        split
                        variant="outline-primary"
                        size="sm"
                        id={`dropdown-${task._id}`}
                      />
                      <Dropdown.Menu align="start">
                        <Dropdown.Header>{t('tasks.table.dropdown.changeStatus')}</Dropdown.Header>
                        <Dropdown.Item
                          onClick={() => onStatusUpdate(task._id, 'pending')}
                          disabled={task.status === 'pending'}
                        >
                          {t('tasks.status.pending')}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => onStatusUpdate(task._id, 'assigned')}
                          disabled={task.status === 'assigned'}
                        >
                          {t('tasks.status.assigned')}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => onStatusUpdate(task._id, 'in-progress')}
                          disabled={task.status === 'in-progress'}
                        >
                          {t('tasks.status.inProgress')}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => onStatusUpdate(task._id, 'completed')}
                          disabled={task.status === 'completed'}
                        >
                          {t('tasks.status.completed')}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => onStatusUpdate(task._id, 'cancelled')}
                          className="text-danger"
                        >
                          {t('tasks.status.cancelAction')}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    {task.status !== 'completed' && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => onComplete(task._id)}
                        title={t('tasks.table.completeTooltip')}
                      >
                        <FaCheckCircle />
                      </Button>
                    )}
                  </ButtonGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Edit Task Modal */}
      {editingTask && (
        <EditTaskModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          task={editingTask}
          onTaskUpdated={handleTaskUpdated}
        />
      )}
    </>
  );
};

export default TaskListTable;
